import React, { useEffect, useState, useCallback } from "react";
import DataGrid, {
	Column,
	Paging,
	FilterRow,
	HeaderFilter,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
// devextreme styles are loaded per-page in this project — mirror Home/Analytics
// so the DataGrid renders correctly even when /broadcasts is the entry point.
import "devextreme/dist/css/dx.light.css";
import {
	getBroadcasts,
	resumeBroadcast,
	cancelBroadcast,
	deleteBroadcastMessages,
} from "../../api/apiServiceFetch";
import BroadcastComposer from "./BroadcastComposer";
import AppBroadcastsHistory from "./AppBroadcastsHistory";
import PushBroadcastsHistory from "./PushBroadcastsHistory";
import "./Broadcasts.scss";

const statusColors = {
	pending: "#888",
	in_progress: "#0a84ff",
	completed: "#22c55e",
	aborted: "#ef4444",
	stalled: "#d97706",
};

// Телеграмная рассылка живёт внутри процесса, который её запустил, и пишет
// heartbeat в updatedAt примерно раз в две секунды. Если он замолчал — процесс
// перезапустили, и рассылка не идёт, хотя в базе она числится выполняющейся.
const STALE_AFTER_MS = 5 * 60 * 1000;

// Бэкенд не возобновляет автоматически рассылки старше этого срока: текст
// обычно привязан ко времени, и разослать его через сутки хуже, чем не слать.
const TOO_OLD_TO_RESUME_MS = 6 * 60 * 60 * 1000;

// Сервер хранит и отдаёт время в UTC, но строку без суффикса зоны браузер
// разберёт как местное время и промахнётся на часовой пояс.
const parseUtc = (value) => {
	if (!value) return null;
	const hasZone = /[zZ]$|[+-]\d{2}:\d{2}$/.test(value);
	return new Date(hasZone ? value : `${value}Z`);
};

const ageMs = (value) => {
	const parsed = parseUtc(value);
	return parsed ? Date.now() - parsed.getTime() : 0;
};

const deriveStatus = (job) => {
	if (job.status === "aborted" || job.status === "completed") return job.status;
	if (job.pending === 0) return "completed";
	if (job.status === "in_progress" && ageMs(job.updatedAt) > STALE_AFTER_MS) {
		return "stalled";
	}
	return job.status;
};

const mediaLabels = {
	0: "Текст",
	1: "Фото",
	2: "Видео",
	3: "Голос",
	4: "Аудио",
};

const statusLabels = {
	pending: "ожидает",
	in_progress: "выполняется",
	completed: "завершена",
	aborted: "прервана",
	stalled: "зависла",
};

/**
 * Экран рассылок. Составление — одно на все способы доставки (сообщение в
 * приложении, уведомление, оба сразу), истории — раздельные, потому что живут
 * они по-разному.
 *
 * Телеграмная рассылка сюда не относится: её запускают из самого бота, а панель
 * только показывает ход и позволяет её остановить.
 */
const Broadcasts = () => {
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [resumingId, setResumingId] = useState(null);
	const [cancellingId, setCancellingId] = useState(null);
	const [deletingId, setDeletingId] = useState(null);

	// Отправка из формы обновляет обе истории: рассылка «в чат + уведомление»
	// создаёт запись в каждой.
	const [reloadToken, setReloadToken] = useState(0);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await getBroadcasts(100, 0);
			setJobs(Array.isArray(data) ? data : []);
		} catch (e) {
			console.error("getBroadcasts failed:", e);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const handleResume = async (job) => {
		if (!job?.id) return;

		// Возобновление старой рассылки немедленно отправит её всем, кто ещё не
		// получил, и отменить это будет уже нельзя. Текст почти всегда привязан
		// ко времени, поэтому спрашиваем явно.
		if (ageMs(job.createdAt) > TOO_OLD_TO_RESUME_MS) {
			const created = parseUtc(job.createdAt);
			const confirmed = window.confirm(
				`Рассылка #${job.id} создана ${created.toLocaleString()} и с тех пор не отправлялась.\n\n` +
					`Возобновление отправит её ${job.pending} пользователям прямо сейчас. ` +
					`Если текст был привязан ко времени, они получат неактуальное сообщение.\n\n` +
					`Продолжить?`
			);
			if (!confirmed) return;
		}

		setResumingId(job.id);
		try {
			await resumeBroadcast(job.id);
			alert(`Возобновление запланировано для рассылки #${job.id}`);
			setTimeout(load, 1500);
		} catch (e) {
			alert("Ошибка: " + (e?.message || e));
		} finally {
			setResumingId(null);
		}
	};

	const handleCancel = async (job) => {
		if (!job?.id) return;
		if (!window.confirm(`Отменить рассылку #${job.id}? Уже отправленные сообщения не будут затронуты.`)) return;
		setCancellingId(job.id);
		try {
			await cancelBroadcast(job.id);
			setTimeout(load, 800);
		} catch (e) {
			alert("Ошибка: " + (e?.message || e));
		} finally {
			setCancellingId(null);
		}
	};

	const handleDeleteMessages = async (job) => {
		if (!job?.id) return;
		if (!window.confirm(`Удалить отправленные сообщения рассылки #${job.id} из чатов пользователей? Telegram позволяет удалять только сообщения младше 48 часов.`)) return;
		setDeletingId(job.id);
		try {
			await deleteBroadcastMessages(job.id);
			alert(`Удаление запланировано для рассылки #${job.id}`);
			setTimeout(load, 1500);
		} catch (e) {
			alert("Ошибка: " + (e?.message || e));
		} finally {
			setDeletingId(null);
		}
	};

	return (
		<div className="broadcasts-page">
			<BroadcastComposer onSent={() => setReloadToken((value) => value + 1)} />

			<AppBroadcastsHistory reloadToken={reloadToken} />

			<PushBroadcastsHistory reloadToken={reloadToken} />

			<div className="broadcasts-page__section">
				<div className="broadcasts-page__section-head">
					<h2>Рассылки в Telegram</h2>
					<Button text="Обновить" onClick={load} disabled={loading} />
				</div>

				<p className="broadcasts-page__hint">
					Эти рассылки запускаются из телеграм-бота, а не отсюда: здесь виден
					только их ход. «Зависла» означает, что сервис перезапустили во время
					отправки — такие рассылки возобновляются сами, если созданы меньше
					шести часов назад, иначе останавливаются и ждут решения.
				</p>

				<DataGrid
					dataSource={jobs}
					keyExpr="id"
					showBorders={true}
					rowAlternationEnabled={true}
				>
					<Paging defaultPageSize={25} />
					<FilterRow visible={true} />
					<HeaderFilter visible={true} />

					<Column dataField="id" caption="ID" width={70} />
					<Column dataField="type" caption="Аудитория" width={100} />
					<Column dataField="sex" caption="Пол" width={70} />
					<Column
						dataField="media"
						caption="Медиа"
						width={90}
						calculateCellValue={(d) => mediaLabels[d.media] ?? d.media}
					/>
					<Column
						dataField="status"
						caption="Статус"
						width={120}
						cellRender={({ data }) => {
							const status = deriveStatus(data);
							return (
								<span
									style={{
										color: statusColors[status] ?? "#000",
										fontWeight: 600,
									}}
									title={
										status === "stalled"
											? "Процесс, который вёл рассылку, был перезапущен. Она возобновится автоматически, если создана меньше 6 часов назад."
											: undefined
									}
								>
									{statusLabels[status] ?? status}
								</span>
							);
						}}
					/>
					<Column
						caption="Прогресс"
						width={100}
						allowFiltering={false}
						calculateCellValue={(d) =>
							d.totalUsers > 0
								? `${Math.round(((d.success + d.failed + d.deleted) / d.totalUsers) * 100)}%`
								: "—"
						}
					/>
					<Column dataField="totalUsers" caption="Всего" width={80} />
					<Column dataField="success" caption="Доставлено" width={110} />
					<Column dataField="failed" caption="Ошибки" width={90} />
					<Column dataField="deleted" caption="Удалены" width={95} />
					<Column dataField="pending" caption="Осталось" width={95} />
					<Column
						dataField="createdAt"
						caption="Создан"
						dataType="datetime"
						format="dd.MM.yyyy HH:mm"
						width={160}
					/>
					<Column dataField="messagePreview" caption="Сообщение" />
					<Column
						caption="Действие"
						width={260}
						cellRender={({ data }) => {
							const canResume = data.pending > 0 && data.status !== "aborted";
							const canCancel = data.status === "in_progress" || (data.status === "pending" && data.pending > 0);
							const canDelete = data.success > 0;
							return (
								<div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
									{canResume && (
										<Button
											text={resumingId === data.id ? "..." : "Возобновить"}
											disabled={resumingId === data.id}
											onClick={() => handleResume(data)}
											type="default"
										/>
									)}
									{canCancel && (
										<Button
											text={cancellingId === data.id ? "..." : "Отменить"}
											disabled={cancellingId === data.id}
											onClick={() => handleCancel(data)}
											type="danger"
											stylingMode="outlined"
										/>
									)}
									{canDelete && (
										<Button
											text={deletingId === data.id ? "..." : "🗑️ Сообщения"}
											disabled={deletingId === data.id}
											onClick={() => handleDeleteMessages(data)}
											type="danger"
										/>
									)}
								</div>
							);
						}}
					/>
				</DataGrid>
			</div>
		</div>
	);
};

export default Broadcasts;
